import { useCallback, useEffect } from 'react';
import { logEvent, setUserProperties, setUserId } from 'firebase/analytics';
import { analytics } from '../firebase';
import { useAuth } from '../contexts/AuthContext';

/**
 * Enhanced Analytics hook for tracking user interactions and business metrics
 * Tracks: Active users, hotel visits, search patterns, user engagement
 */
const useAnalytics = () => {
  const { user } = useAuth();

  // Set user properties when user changes
  useEffect(() => {
    if (analytics && user) {
      setUserId(analytics, user.uid);
      setUserProperties(analytics, {
        user_type: 'authenticated',
        user_email_domain: user.email?.split('@')[1] || 'unknown',
        signup_method: 'google'
      });
    }
  }, [user]);

  const trackEvent = useCallback((eventName, parameters = {}) => {
    if (!analytics) {
      console.warn('Analytics not initialized, event not tracked:', eventName);
      return;
    }

    try {
      const enrichedParams = {
        ...parameters,
        timestamp: Date.now(),
        user_id: user?.uid || 'anonymous',
        user_email: user?.email || 'anonymous',
        app_version: process.env.REACT_APP_VERSION || '1.0.0'
      };

      logEvent(analytics, eventName, enrichedParams);
      console.log(`📊 Analytics: ${eventName}`, enrichedParams);
    } catch (error) {
      console.error('Failed to track event:', eventName, error);
    }
  }, [user]);

  // Page view tracking
  const trackPageView = useCallback((pageName, additionalData = {}) => {
    trackEvent('page_view', {
      page_title: pageName,
      page_location: window.location.href,
      page_path: window.location.pathname,
      ...additionalData
    });
  }, [trackEvent]);

  // User action tracking
  const trackUserAction = useCallback((action, target = null, additionalData = {}) => {
    trackEvent('user_action', {
      action_name: action,
      action_target: target,
      ...additionalData
    });
  }, [trackEvent]);

  // Login tracking
  const trackLogin = useCallback((method = 'google') => {
    trackEvent('login', {
      method,
      login_time: new Date().toISOString()
    });
  }, [trackEvent]);

  // Enhanced logout tracking
  const trackLogout = useCallback((reason = 'user_initiated') => {
    const sessionStart = parseInt(localStorage.getItem('session_start')) || Date.now();
    const sessionDuration = Date.now() - sessionStart;
    
    trackEvent('logout', {
      logout_time: new Date().toISOString(),
      session_duration: sessionDuration,
      session_duration_minutes: Math.round(sessionDuration / 60000),
      logout_reason: reason
    });
    
    // Clear session data
    localStorage.removeItem('session_start');
  }, [trackEvent]);

  // Hotel visit tracking - Key metric for your business
  const trackHotelVisit = useCallback((hotelId, hotelName = null, additionalData = {}) => {
    trackEvent('hotel_visit', {
      hotel_id: hotelId,
      hotel_name: hotelName,
      visit_time: new Date().toISOString(),
      ...additionalData
    });

    // Also track as a custom conversion event
    trackEvent('hotel_viewed', {
      hotel_id: hotelId,
      hotel_name: hotelName,
      value: 1, // For conversion tracking
      currency: 'USD'
    });
  }, [trackEvent]);

  // Hotel search tracking
  const trackHotelSearch = useCallback((searchTerm, resultCount = null, additionalData = {}) => {
    trackEvent('search', {
      search_term: searchTerm,
      search_results_count: resultCount,
      search_time: new Date().toISOString(),
      ...additionalData
    });
  }, [trackEvent]);

  // Feature usage tracking
  const trackFeatureUsage = useCallback((featureName, additionalData = {}) => {
    trackEvent('feature_usage', {
      feature_name: featureName,
      usage_time: new Date().toISOString(),
      ...additionalData
    });
  }, [trackEvent]);

  // Business metrics tracking
  const trackBusinessEvent = useCallback((eventType, data = {}) => {
    trackEvent('business_event', {
      event_type: eventType,
      ...data,
      business_time: new Date().toISOString()
    });
  }, [trackEvent]);

  // Export data tracking - KEY BUSINESS METRIC
  const trackExport = useCallback((exportType, itemCount = 0, additionalData = {}) => {
    trackEvent('export_data', {
      export_type: exportType,
      item_count: itemCount,
      export_time: new Date().toISOString(),
      ...additionalData
    });
    
    // Also track as business event for revenue tracking
    trackEvent('business_action', {
      action: 'data_export',
      export_format: exportType,
      record_count: itemCount,
      value: 1 // Could be used for conversion tracking
    });
  }, [trackEvent]);

  // Session start tracking
  const trackSessionStart = useCallback(() => {
    const sessionStart = Date.now();
    localStorage.setItem('session_start', sessionStart.toString());
    
    trackEvent('session_start', {
      session_id: sessionStart.toString(),
      start_time: new Date().toISOString()
    });
  }, [trackEvent]);

  // Navigation tracking
  const trackNavigation = useCallback((from, to, method = 'click') => {
    trackEvent('navigation', {
      from_page: from,
      to_page: to,
      navigation_method: method,
      navigation_time: new Date().toISOString()
    });
  }, [trackEvent]);

  // Error tracking
  const trackError = useCallback((errorType, errorMessage, context = {}) => {
    trackEvent('error_occurred', {
      error_type: errorType,
      error_message: errorMessage,
      error_time: new Date().toISOString(),
      ...context
    });
  }, [trackEvent]);

  return {
    trackPageView,
    trackUserAction,
    trackLogin,
    trackLogout,
    trackHotelVisit,
    trackHotelSearch,
    trackFeatureUsage,
    trackBusinessEvent,
    trackExport,
    trackSessionStart,
    trackNavigation,
    trackError,
    trackEvent
  };
};

export default useAnalytics;